/**
 * legalAgreementsClient — the browser's side of an owner's confirmation that the firm has a research provider's
 * written permission (legal apps delivery 8; Miles, 2026-09-28, ruling 2: "Firm confirms, Prest0n drives";
 * docs/PLAN-legal-apps-20260928.md §E).
 *
 * THE SERVER DECIDES EVERYTHING (functions/src/legalApps/legalAgreementEndpoints.ts): which services need a
 * confirmation, which are confirmed in this workspace, who may confirm or withdraw, and the exact statement to
 * confirm. The browser reads that answer strictly and shows it; a read that fails is null, which every caller treats
 * as "nothing confirmed" — the refusal the platform gave before an owner could confirm.
 *
 * Firebase loads when a call is made, never when this module loads: the sign-in helpers that ask it ride the first
 * download, and the chat suites load the legal apps rows without a Firebase app.
 */

export const LEGAL_AGREEMENTS_GET_CALLABLE = 'legalAppsGetAgreements';
export const LEGAL_AGREEMENTS_SET_CALLABLE = 'legalAppsSetAgreement';

export type LegalAgreementViewState = 'confirmed' | 'not-confirmed' | 'withdrawn' | 'unknown';

export interface LegalAgreementAppView {
  readonly key: string;
  readonly name: string;
  readonly vendor: string;
  readonly state: LegalAgreementViewState;
  readonly version: number;
  readonly confirmedByName?: string;
  readonly confirmedAtMs?: number;
  readonly withdrawnByName?: string;
  readonly withdrawnAtMs?: number;
  /** The exact words an owner confirms, from the server. */
  readonly statement: string;
}

export interface LegalAgreementsView {
  readonly accountId: string;
  readonly readable: boolean;
  readonly canConfirm: boolean;
  readonly canWithdraw: boolean;
  readonly statementVersion: number;
  readonly apps: readonly LegalAgreementAppView[];
}

const STATES: ReadonlySet<string> = new Set(['confirmed', 'not-confirmed', 'withdrawn', 'unknown']);
const KEY_RE = /^[a-z0-9_]{1,40}$/;

const str = (v: unknown, max: number): string => (typeof v === 'string' ? v.slice(0, max) : '');
const whole = (v: unknown): number | undefined => (typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : undefined);

/** PURE. The server's answer, read strictly: anything malformed is null, and a malformed app is left out. */
export function parseLegalAgreementsView(raw: unknown): LegalAgreementsView | null {
  if (!raw || typeof raw !== 'object') return null;
  const d = raw as Record<string, unknown>;
  const statementVersion = whole(d.statementVersion);
  if (statementVersion === undefined || !Array.isArray(d.apps)) return null;
  const apps: LegalAgreementAppView[] = [];
  for (const a of d.apps.slice(0, 20)) {
    if (!a || typeof a !== 'object') continue;
    const r = a as Record<string, unknown>;
    const key = str(r.key, 40);
    const state = str(r.state, 20);
    const statement = str(r.statement, 2000);
    if (!KEY_RE.test(key) || !STATES.has(state) || !statement) continue;
    const confirmedAtMs = whole(r.confirmedAtMs);
    const withdrawnAtMs = whole(r.withdrawnAtMs);
    apps.push({
      key,
      name: str(r.name, 80) || key,
      vendor: str(r.vendor, 80),
      state: state as LegalAgreementViewState,
      version: whole(r.version) ?? 0,
      ...(str(r.confirmedByName, 120) ? { confirmedByName: str(r.confirmedByName, 120) } : {}),
      ...(confirmedAtMs !== undefined ? { confirmedAtMs } : {}),
      ...(str(r.withdrawnByName, 120) ? { withdrawnByName: str(r.withdrawnByName, 120) } : {}),
      ...(withdrawnAtMs !== undefined ? { withdrawnAtMs } : {}),
      statement,
    });
  }
  return {
    accountId: str(d.accountId, 128),
    readable: d.readable === true,
    canConfirm: d.canConfirm === true,
    canWithdraw: d.canWithdraw === true,
    statementVersion,
    apps,
  };
}

/** PURE. The services confirmed in this workspace: only from a readable answer. */
export function confirmedKeysOf(view: LegalAgreementsView | null | undefined): ReadonlySet<string> {
  if (!view || !view.readable) return new Set();
  return new Set(view.apps.filter((a) => a.state === 'confirmed').map((a) => a.key));
}

type Call = (name: string, payload: Record<string, unknown>) => Promise<unknown>;

/** The default call: the app's own Functions instance, loaded when first used. */
async function defaultCall(name: string, payload: Record<string, unknown>): Promise<unknown> {
  const [{ getFunctions, httpsCallable }, { getApp }] = await Promise.all([import('firebase/functions'), import('firebase/app')]);
  const result = await httpsCallable(getFunctions(getApp(), 'us-central1'), name)(payload);
  return result.data;
}

/** This workspace's confirmations (`accountId` the workspace, else the server's own choice), or null. */
export async function fetchLegalAgreements(opts: { accountId?: string | null; lang?: 'en' | 'es' } = {}, call: Call = defaultCall): Promise<LegalAgreementsView | null> {
  try {
    const payload: Record<string, unknown> = { lang: opts.lang === 'es' ? 'es' : 'en' };
    if (opts.accountId) payload.accountId = opts.accountId;
    return parseLegalAgreementsView(await call(LEGAL_AGREEMENTS_GET_CALLABLE, payload));
  } catch {
    return null;
  }
}

export type SetLegalAgreementResult =
  | { readonly ok: true; readonly changed: boolean; readonly state: string; readonly version: number }
  | { readonly ok: false; readonly message: string | null };

/** Confirm or withdraw. A refusal carries the server's own words; a transport failure carries none. */
export async function setLegalAgreement(
  opts: {
    accountId?: string | null;
    appKey: string;
    action: 'confirm' | 'withdraw';
    statementVersion: number;
    expectedVersion: number;
    lang?: 'en' | 'es';
  },
  call: Call = defaultCall,
): Promise<SetLegalAgreementResult> {
  try {
    const payload: Record<string, unknown> = {
      appKey: opts.appKey,
      action: opts.action,
      statementVersion: opts.statementVersion,
      expectedVersion: opts.expectedVersion,
      lang: opts.lang === 'es' ? 'es' : 'en',
    };
    if (opts.accountId) payload.accountId = opts.accountId;
    const data = (await call(LEGAL_AGREEMENTS_SET_CALLABLE, payload)) as Record<string, unknown> | null;
    // A change is read fresh by every card that shows the service next.
    forgetSharedLegalAgreements();
    return {
      ok: true,
      changed: Boolean(data && data.changed === true),
      state: data && typeof data.state === 'string' ? data.state : '',
      version: data && typeof data.version === 'number' ? data.version : 0,
    };
  } catch (err: unknown) {
    const message = err && typeof err === 'object' && typeof (err as { message?: unknown }).message === 'string'
      ? String((err as { message: string }).message).slice(0, 300)
      : null;
    return { ok: false, message };
  }
}

/** How long one read of the confirmations serves every chat card that shows a research service. */
export const LEGAL_AGREEMENTS_SHARED_FRESH_MS = 10_000;

let shared: { readonly key: string; readonly atMs: number; readonly promise: Promise<LegalAgreementsView | null> } | null = null;

/**
 * The confirmations for the chat's cards: one read serves every card for a few seconds (a thread can hold several
 * cards, and each reads again when the window returns). A read that failed is not kept. The Integrations page and
 * the sign-in window read fresh (fetchLegalAgreements, researchConfirmedHere): they act on the answer.
 */
export function readLegalAgreementsShared(
  accountId?: string | null,
  nowMs: number = Date.now(),
  call: Call = defaultCall,
): Promise<LegalAgreementsView | null> {
  const key = accountId || '';
  if (shared && shared.key === key && nowMs - shared.atMs < LEGAL_AGREEMENTS_SHARED_FRESH_MS) return shared.promise;
  const promise = fetchLegalAgreements({ accountId }, call);
  const entry = { key, atMs: nowMs, promise };
  shared = entry;
  void promise.then((view) => {
    if (view === null && shared === entry) shared = null;
  });
  return promise;
}

/** Forget the shared read (after a change, and between tests). */
export function forgetSharedLegalAgreements(): void {
  shared = null;
}

/**
 * Whether an owner of the workspace has confirmed the firm's permission for one research service. Anything but a
 * readable "confirmed" is false.
 */
export async function researchConfirmedHere(appKey: string, accountId?: string | null, call: Call = defaultCall): Promise<boolean> {
  const view = await fetchLegalAgreements({ accountId }, call);
  return confirmedKeysOf(view).has(appKey);
}
